/* eslint-disable react/jsx-no-useless-fragment */
import Input from '@/shared/components/rebranded/Input/Input';
// import InlineAlert from '@storeblocks/inline-alert';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { FormEvent, useEffect, useState } from 'react';
import { FieldSet, Legend } from '../../../shared/components/Form/Form';
import {
  InputElementContainer,
  InputHint
} from '../../../shared/components/Input';
import { Label } from '../../../shared/components/Label/Label';
import { FieldProps, NumberSchema } from '../../types';
import { joinErrors } from '../../validation/utils';
import { hasErrors } from '../../validation/validateSchema';
import styles from './NumberInputField.module.css';

export type NumberInputProps = FieldProps<NumberSchema> & {
  integerLabel?: string;
  decimalLabel?: string;
};

export const NumberInputField = ({
  schema,
  name,
  value,
  touched,
  errors,
  id,
  onBlur,
  onChange,
  forceShowError,
  readOnly,
  integerLabel,
  decimalLabel
}: NumberInputProps) => {
  const { getText } = useTranslations();
  const [integerPart, setIntegerPart] = useState(
    value == null ? null : formatInteger(getInteger(value))
  );
  const [decimalPart, setDecimalPart] = useState<string | null>(
    value == null ? '00' : formatDecimal(getDecimal(value))
  );

  const isDecimalPartDisabled = !!value && value >= 100000000;
  useEffect(() => {
    if (isDecimalPartDisabled) {
      setDecimalPart('00');
    }
  }, [isDecimalPartDisabled]);

  const $uiOptions = schema.$uiOptions || {};
  const showError = !!((touched || forceShowError) && hasErrors(errors));

  const handleBlur = () => {
    const integer = toInteger(normalize(integerPart));
    const decimal = toDecimal(normalize(decimalPart)) ?? 0;

    if (integer) {
      setIntegerPart(formatInteger(integer));
    }
    setDecimalPart(formatDecimal(decimal));
    onBlur({ target: { name, touched: true } });
  };

  const handleFocus = () => {
    if (decimalPart === '00') {
      setDecimalPart('');
    }
  };

  const handleIntegerChange = (e: FormEvent<HTMLInputElement>) => {
    const integer = normalize(e.currentTarget.value);

    setIntegerPart(integer);

    const integerValue = toInteger(integer);
    const decimalValue = toDecimal(normalize(decimalPart)) ?? 0;
    const newValue = combine(integerValue, decimalValue);
    onChange({ target: { name, value: newValue } });
  };

  const handleDecimalChange = (e: FormEvent<HTMLInputElement>) => {
    const decimal = normalize(e.currentTarget.value.substring(0, 2));

    setDecimalPart(decimal);

    const integerValue = toInteger(normalize(integerPart)) ?? 0;
    const decimalValue = toDecimal(decimal);
    const newValue = combine(integerValue, decimalValue);
    onChange({ target: { name, value: newValue } });
  };

  const minimum = 0;
  const maximum = Number.MAX_SAFE_INTEGER;

  const handleIntegerKeyDown = (e: any) => {
    switch (e.key) {
      case 'ArrowDown': {
        const integer = toInteger(normalize(integerPart)) ?? 0;
        const decimal = toDecimal(normalize(decimalPart)) ?? 0;
        const newValue = combine(Math.max(integer - 1, minimum), decimal);
        if (newValue === value) {
          break;
        }

        setIntegerPart(formatInteger(getInteger(newValue)));
        onChange({ target: { name, value: newValue } });
        break;
      }

      case 'ArrowUp': {
        const integer = toInteger(normalize(integerPart)) ?? 0;
        const decimal = toDecimal(normalize(decimalPart)) ?? 0;
        const newValue = combine(Math.min(integer + 1, maximum), decimal);
        if (newValue === value) {
          break;
        }

        setIntegerPart(formatInteger(getInteger(newValue)));
        onChange({ target: { name, value: newValue } });
        break;
      }

      default:
    }
  };

  const handleDecimalKeyDown = (e: any) => {
    switch (e.key) {
      case 'ArrowDown': {
        const integer = toInteger(normalize(integerPart)) ?? 0;
        const decimal = toDecimal(normalize(decimalPart)) ?? 0;
        const newValue = combine(integer, Math.max(decimal - 1, 0));
        if (newValue === value) {
          break;
        }

        setDecimalPart(formatDecimal(getDecimal(newValue)));
        onChange({ target: { name, value: newValue } });
        break;
      }

      case 'ArrowUp': {
        const integer = toInteger(normalize(integerPart)) ?? 0;
        const decimal = toDecimal(normalize(decimalPart)) ?? 0;
        const newValue = combine(integer, Math.min(decimal + 1, 99));
        if (newValue === value) {
          break;
        }

        setDecimalPart(formatDecimal(getDecimal(newValue)));
        onChange({ target: { name, value: newValue } });
        break;
      }
      default:
    }
  };

  const error = showError ? joinErrors(errors, getText) : undefined;

  const label = (
    <Label title={schema.title} description={schema.description} id={id} />
  );

  return (
    <FieldSet>
      <Legend>{label}</Legend>
      {$uiOptions.hint && <InputHint id={id} hint={$uiOptions.hint} />}
      <InputElementContainer
        className={styles.inputContainer}
        error={!!error}
        style={{ marginTop: $uiOptions.hint ? 0 : 8 }}
      >
        <div className={styles.integerContainer}>
          <Input
            type="text"
            inputMode="numeric"
            id={`${id}-integer`}
            name={`${name}-integer`}
            label={integerLabel ?? 'Heltall'}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-describedby': schema.description
                ? `${id}_description`
                : undefined,
              autoFocus: $uiOptions.autoFocus,
              minLength: 0,
              maxLength: 15
            }}
            value={integerPart ?? ''}
            error={error}
            onBlur={handleBlur}
            onChange={handleIntegerChange}
            onKeyDown={handleIntegerKeyDown}
            disabled={readOnly}
          />
        </div>
        <div className={styles.decimalContainer}>
          <Input
            type="text"
            inputMode="numeric"
            id={`${id}-decimal`}
            name={`${name}-decimal`}
            label={decimalLabel ?? 'Desimaler'}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-describedby': schema.description
                ? `${id}_description`
                : undefined,
              autoFocus: $uiOptions.autoFocus,
              minLength: 2,
              maxLength: 2
            }}
            value={decimalPart ?? ''}
            error={error}
            onBlur={handleBlur}
            onChange={handleDecimalChange}
            onKeyDown={handleDecimalKeyDown}
            onFocus={handleFocus}
            disabled={readOnly || isDecimalPartDisabled}
          />
        </div>
      </InputElementContainer>
    </FieldSet>
  );
};

function toInteger(value: null): null;
function toInteger(value: string): number;
function toInteger(value: string | null): number | null;
function toInteger(value: string | null): number | null {
  if (value == null) {
    return null;
  }

  return parseInt(value, 10);
}

function toDecimal(value: null): null;
function toDecimal(value: string): number;
function toDecimal(value: string | null): number | null;
function toDecimal(value: string | null): number | null {
  if (value == null) {
    return null;
  }

  const parsedValue = parseInt(value, 10);

  // Append 0 to decimal if [0.]5 to get [0.]50,
  // otherwise we just want [0.0]5
  return parsedValue < 10 && value.length === 1
    ? 10 * parsedValue
    : parsedValue;
}

function normalize(value: null): null;
function normalize(value: string): string;
function normalize(value: string | null): string | null;
function normalize(value: string | null): string | null {
  if (value == null) {
    return null;
  }

  let filtered = value;

  // If the user paste 1300,50 we strip it
  // so it becomes 1300 instead of 130050;
  const commaIndex = value.lastIndexOf(',');

  if (commaIndex > -1) {
    filtered = filtered.slice(0, commaIndex);
  }

  filtered = filtered.replace(/\D+/g, '');

  if (filtered.trim() === '') {
    return null;
  }

  // Avoid large numbers
  const capped = filtered.slice(0, 15);

  return capped;
}

const getInteger = (value: number): number => {
  return Math.floor(value);
};

const getDecimal = (value: number): number => {
  return Math.round(100 * parseFloat((value % 1).toFixed(2)));
};

const combine = (integer: number, decimal: number): number => {
  return integer + decimal / 100;
};

const formatInteger = (integer: number): string => {
  return `${integer}`
    .split('')
    .reverse()
    .reduce((acc, int, index) =>
      index % 3 === 0 ? `${int} ${acc}` : `${int}${acc}`
    );
};

const formatDecimal = (decimal: number): string => {
  return decimal < 10 ? `0${decimal}` : `${decimal}`;
};
